import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/api";
import { useFeatureIsOn } from "@/app/featureFlags";
import { platformApi } from "@/app/platform";
import { CreditCardsConsentError } from "@/errors/CreditCardsConsentError";
import {
  BnplTrackingEvents,
  CommonTrackingEvents,
  rudderAnalytics,
} from "@/lib/rudderstack";
import type { CustomerEligibilityOutput } from "@/types/types";

export const useIsUserEligible = () => {
  const isBnplFeatureEnabled = useFeatureIsOn("bnpl");

  return useQuery({
    queryKey: ["eligible"],
    queryFn: async ({ signal }) => {
      try {
        const payload = await platformApi.prepareEligibilityCheck();

        if (!isBnplFeatureEnabled) {
          return { isEligible: false } as const;
        }

        const data = await api
          .patch<{
            data: CustomerEligibilityOutput;
          }>(`/transactions/bnpl/eligibility`, payload, { signal })
          .then((res) => res.data.data);

        const eventName = data.isEligible
          ? BnplTrackingEvents.USER_PASSED_ELIGIBILITY_CHECK
          : BnplTrackingEvents.USER_FAILED_ELIGIBILITY_CHECK;

        rudderAnalytics.track(eventName);

        return data;
      } catch (error) {
        if (
          error instanceof CreditCardsConsentError &&
          error.cause === "cancelled"
        ) {
          rudderAnalytics.track(CommonTrackingEvents.USER_REJECTED_CONSENT, {
            scope: "credit_card_information",
          });
        }

        throw error;
      }
    },
    retry: false,
    throwOnError: false,
  });
};
